import { recaptchaEnabled } from 'helpers/recaptcha';
import { phoneValidator, auctionAccessIdValidator } from 'helpers/regex';
import {
	shouldRequireAuctionAccessDealerId,
	shouldUseDriversLicenseFallbackForAuctionAccessDealerId,
} from '../auctionAccessRules';
import { addressValidations } from './stepOne';
import { requiredLicenseFile } from './utilities';
import { DOCUMENT_TYPES } from 'constants/signUp';
import * as Yup from 'yup';

const fullyValidateAuctionAccessDealerId = (t) =>
	Yup.string()
		.nullable()
		.required(t('signup:steps.step0.auctionAccessDealerIdRequired'))
		.test(
			'invalidAuctionAccessDealerId',
			t('signup:steps.step0.invalidAuctionAccessDealerId'),
			auctionAccessIdValidator,
		);

const onlyValidateAuctionAccessDealerIdFormat = (t) =>
	Yup.string()
		.nullable()
		.test(
			'invalidAuctionAccessDealerId',
			t('signup:steps.step0.invalidAuctionAccessDealerId'),
			auctionAccessIdValidator,
		);

export const validationFormStepZero = ({
	t,
	signUpPolicies = [],
	isFastPath,
	isAfcFastPath,
	isAffiliatePartner,
	isRecaptchaV2,
	driversLicenseInStepZero,
}) => {
	const schema = {
		firstName: Yup.string().nullable().required(t('signup:steps.step0.nameRequired')),
		lastName: Yup.string().nullable().required(t('signup:steps.step0.nameRequired')),
		email: Yup.string().nullable().email().required(t('common:emailRequired')),
		mobilePhone: Yup.string()
			.nullable()
			.required(t('common:phoneRequired'))
			.test('invalidMobilePhone', t('signup:steps.step1.invalidPhone'), phoneValidator),
		dealershipName: Yup.string().nullable().required(t('signup:steps.step0.nameRequired')),
		hasUSLicense: Yup.string().nullable().required(t('common:selectionRequired')),
		businessType: Yup.string().nullable().required(t('common:selectionRequired')),
		auctionAccessDealerId: onlyValidateAuctionAccessDealerIdFormat(t),
		driverLicenseFile: Yup.mixed().nullable(),
		stateTaxId:
			isAfcFastPath ?
				Yup.string().nullable().required(t('signup:steps.step0.stateTaxIdRequired'))
			:	Yup.string().nullable(),
		recaptcha:
			recaptchaEnabled && isRecaptchaV2 ?
				Yup.string().nullable().required(t('common:recaptchaError'))
			:	Yup.string().nullable(),
		...(isFastPath || isAfcFastPath || isAffiliatePartner ? addressValidations({ t }) : {}),
	};

	const requireAuctionAccessDealerId = shouldRequireAuctionAccessDealerId({
		isAfcFastPath,
		driversLicenseInStepZero,
	});
	const useDriversLicenseFallbackForAuctionAccessDealerId = shouldUseDriversLicenseFallbackForAuctionAccessDealerId({
		isAfcFastPath,
		driversLicenseInStepZero,
	});

	if (useDriversLicenseFallbackForAuctionAccessDealerId) {
		// if the driver's license and auction access id is displayed, the user must complete one or the other
		schema.driverLicenseFile = Yup.mixed().when('auctionAccessDealerId', {
			is: '',
			then: requiredLicenseFile('driverLicenseFileLast', t, t('signup:steps.step3:driverLicenseRequired')),
			otherwise: Yup.mixed().nullable(),
		});
		schema.auctionAccessDealerId = Yup.string().when('driverLicenseFile', {
			is: (driverLicenseFile) => !driverLicenseFile,
			then: fullyValidateAuctionAccessDealerId(t),
			otherwise: onlyValidateAuctionAccessDealerIdFormat(t),
		});
	} else if (requireAuctionAccessDealerId) {
		schema.auctionAccessDealerId = fullyValidateAuctionAccessDealerId(t);
	}

	if (signUpPolicies) {
		signUpPolicies.forEach(({ acceptance_required: required, document_type, checkbox_label }) => {
			if (document_type && checkbox_label) {
				if (required && document_type !== DOCUMENT_TYPES.afc_privacy_policy) {
					schema[document_type] = Yup.object()
						.nullable()
						.required(t('signup:steps.step0.privacyPolicyRequired'));
				}
				// validate afc_privacy_policy only if it's an AFC fast path sign up even if the server does not say it is required
				if (isAfcFastPath && document_type == DOCUMENT_TYPES.afc_privacy_policy) {
					schema[document_type] = Yup.object()
						.nullable()
						.required(t('signup:steps.step0.privacyPolicyRequired'));
				}
			}
		});
	}

	/**
	 * Undocumented fix for yup's cyclic dependency error between auctionAccessDealerId and driverLicenseFile
	 * Technique described here: https://github.com/jquense/yup/issues/1006
	 */
	const noSortEdges = ['auctionAccessDealerId', 'driverLicenseFile'];
	return Yup.object().shape(schema, noSortEdges);
};
